import React, { useState, useRef, useCallback, useEffect } from 'react';
import apiService from '../services/ApiService';
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from '../constants/uploadLimits';
import { ImageCropModal } from './ui/ImageCropModal';
import { AvatarImage } from './ui/AvatarImage';
import { prepareImageFileForUpload } from '../utils/prepareImageFileForUpload';
import { hasNativeCapability, requestNativePhoto } from '../utils/nativeShell';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  currentPosition?: { x: number; y: number; scale: number };
  onAvatarUpdate: (avatarUrl: string | null, position?: { x: number; y: number; scale: number }) => void;
  onError: (error: string) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  currentPosition,
  onAvatarUpdate,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAvatarUrl || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const nativeCamera = hasNativeCapability('native_camera');

  useEffect(() => {
    setPreviewUrl(currentAvatarUrl || null);
  }, [currentAvatarUrl]);

  const handleNativeAvatar = async (source: 'camera' | 'library') => {
    if (isPreparing || isUploading) return;
    setIsUploading(true);
    try {
      const result = await requestNativePhoto({ purpose: 'avatar', source });
      if (!result.ok) {
        if (result.error === 'cancelled') return;
        onError(result.error || 'Camera capture failed');
        return;
      }
      if (result.uri) {
        setPreviewUrl(result.uri);
        onAvatarUpdate(result.uri);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const validateFile = useCallback(
    (file: File): boolean => {
      const typeOk =
        file.type.startsWith('image/') ||
        !file.type ||
        /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
      if (!typeOk) {
        onError('Please upload a JPEG, PNG, or WebP image');
        return false;
      }
      return true;
    },
    [onError]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!validateFile(file) || isPreparing || isUploading) return;
      void (async () => {
        setIsPreparing(true);
        try {
          const prepared = await prepareImageFileForUpload(file, {
            maxLongEdge: 2048,
            maxBytes: IMAGE_UPLOAD_MAX_BYTES,
          });
          setCropFile(prepared);
          setCropOpen(true);
        } catch {
          onError("We couldn't prepare this photo. Try another image.");
        } finally {
          setIsPreparing(false);
        }
      })();
    },
    [validateFile, isPreparing, isUploading, onError]
  );

  const handleCropConfirm = async ({ file }: { file: File }) => {
    setIsUploading(true);
    try {
      const result = await apiService.uploadAvatar(file);
      setPreviewUrl(result.avatar_url);
      onAvatarUpdate(result.avatar_url, result.position);
      setCropOpen(false);
      setCropFile(null);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteAvatar();
      setPreviewUrl(null);
      onAvatarUpdate(null);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <AvatarImage
          src={previewUrl}
          alt="Profile photo"
          className="w-24 h-24 border-2 border-white/[0.10]"
          position={currentPosition}
        />

        <div className="flex-1">
          <h3 className="text-lg font-medium text-white mb-2">Profile Picture</h3>
          <p className="text-sm text-gray-400 mb-4">
            {previewUrl ? 'Change your profile picture' : 'Upload a profile picture to replace the default avatar'}
          </p>

          {!previewUrl ? (
            <div className="space-y-3">
              {nativeCamera ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-primary btn-sm min-h-[44px]"
                    disabled={isPreparing || isUploading}
                    onClick={() => void handleNativeAvatar('camera')}
                  >
                    Take photo
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm min-h-[44px]"
                    disabled={isPreparing || isUploading}
                    onClick={() => void handleNativeAvatar('library')}
                  >
                    Choose from library
                  </button>
                </div>
              ) : null}
            <div
              className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragOver ? 'border-orange-400 bg-orange-900/15' : 'border-white/[0.10] hover:border-white/[0.18]'
              } ${isPreparing ? 'pointer-events-none opacity-60' : ''}`}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file) handleFileSelect(file);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              onClick={() => !isPreparing && fileInputRef.current?.click()}
            >
              <p className="text-sm text-gray-300">
                {isPreparing ? 'Preparing image…' : nativeCamera ? 'Or use file picker' : 'Drop an image here or click to select'}
              </p>
              <p className="text-xs text-gray-500 mt-1">JPEG, PNG, WebP up to {IMAGE_UPLOAD_MAX_MB}MB</p>
            </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nativeCamera ? (
                <button
                  type="button"
                  onClick={() => void handleNativeAvatar('camera')}
                  disabled={isPreparing || isUploading}
                  className="btn-primary btn-sm min-h-[44px]"
                >
                  Take photo
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPreparing || isUploading}
                className="btn-primary btn-sm"
              >
                {isPreparing ? 'Preparing image…' : 'Change Picture'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting || isPreparing}
                className="btn-ghost btn-sm text-red-300"
              >
                {isDeleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </div>
      </div>

      <ImageCropModal
        isOpen={cropOpen}
        onClose={() => {
          setCropOpen(false);
          setCropFile(null);
        }}
        file={cropFile}
        presetId="avatar"
        onConfirm={handleCropConfirm}
        busy={isUploading}
      />
    </div>
  );
};
